import React from "react";
import { createPortal } from "react-dom";
import { db } from "../../../../firebase/firebaseConfig";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import dayjs from "dayjs";
import { FiX } from "react-icons/fi";

export default function AttemptsDrawer({ open, onClose, student, assignment }) {
  const [attempts, setAttempts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !student || !assignment) return;
    setLoading(true);
    const qRef = query(
      collection(db, "test_histories"),
      where("userId", "==", student.id),
      where("assignmentId", "==", assignment.id),
      orderBy("submittedAt", "desc")
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAttempts(arr);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [open, student, assignment]);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  if (!open) return null;

  const fmtTs = (ts) => (ts?.seconds ? dayjs(ts.seconds * 1000).format("DD/MM/YYYY HH:mm") : "-");
  const toPercent = (h) =>
    typeof h.percent === "number"
      ? h.percent
      : h?.score?.total > 0
      ? Math.round((h.score.correct / h.score.total) * 100)
      : 0;

  const content = (
    <div className="fixed inset-0 z-[9999] flex">
      <div className="absolute inset-0 bg-black/40 z-10" onClick={onClose} />
      <div
        className="relative z-20 ml-auto h-full w-[min(720px,95vw)] bg-card border-l border-border shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <div className="font-bold">Lần nộp của học viên</div>
            <div className="text-xs text-text2">
              {student?.fullName || `${student?.firstName || ""} ${student?.lastName || ""}`.trim()} • Test:{" "}
              <span className="font-mono">{assignment?.testId}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-bg" aria-label="Đóng">
            <FiX />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto" style={{ maxHeight: "calc(100vh - 60px)" }}>
          {loading && <div className="text-sm text-text2">Đang tải...</div>}
          {!loading && attempts.length === 0 && (
            <div className="text-sm text-text2">Chưa có lần nộp nào cho assignment này.</div>
          )}

          {attempts.map((h, idx) => {
            const pct = toPercent(h);
            const correct = h?.score?.correct ?? "-";
            const total = h?.score?.total ?? "-";
            const duration = typeof h?.duration === "number" ? `${Math.round(h.duration / 60)}′` : "-";
            const status = h?.status || "finished";
            return (
              <div
                key={h.id}
                className="rounded-xl border border-border bg-bg/40 p-3 flex items-center justify-between hover:bg-primary/5"
              >
                <div className="space-y-1">
                  <div className="text-sm">
                    <span className="font-semibold">Lần {idx + 1}</span> • {fmtTs(h.submittedAt)} •{" "}
                    <span className="text-text2">Trạng thái: {status}</span>
                  </div>
                  <div className="text-xs text-text2">
                    Điểm: <b>{correct}/{total}</b> ({pct}%) • Thời gian: {duration} • Parts: {h.parts ?? "-"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/ket-qua/${h.testId}?historyId=${h.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-xl border border-border hover:bg-primary/10 text-sm text-primary underline"
                  >
                    Xem kết quả
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
